import React from 'react';
import styled from 'styled-components';
import { Dialog, DialogContent } from '@mui/material';
import { stimulusFileUrl } from '../../utils/stimulusFileUrl';

const renderPreviewBody = (file) => {
  if (file.fileType === 'IMAGE') {
    return <PreviewImage src={stimulusFileUrl(file.url)} alt={file.name} />;
  }
  if (file.fileType === 'TEXT') {
    if (file.text != null) return <PreviewText>{file.text}</PreviewText>;
    return <PreviewText dangerouslySetInnerHTML={{ __html: file.html }} />;
  }
  return <PreviewFrame src={stimulusFileUrl(file.url)} title={file.name} />;
};

const FilePreviewModal = ({ open, onClose, file, reader = false, footer, busy = false }) => {
  const titleId = React.useId();
  if (!file) {
    return null;
  }

  return (
    <StyledDialog $reader={reader} open={open} onClose={onClose} maxWidth={false} aria-labelledby={titleId}>
      <StyledDialogContent>
        <ModalContainer $reader={reader} aria-busy={busy}>
          <ModalHeader>
            <HeaderTitle id={titleId} title={file.name}>{file.name}</HeaderTitle>
            <CloseButton type="button" onClick={onClose} aria-label="Close">
              ×
            </CloseButton>
          </ModalHeader>
          <ModalBody $image={reader && file.fileType === 'IMAGE'}>{renderPreviewBody(file)}</ModalBody>
          {footer && <ModalFooter>{footer}</ModalFooter>}
        </ModalContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default FilePreviewModal;

const StyledDialog = styled(Dialog)`
  && .MuiDialog-paper {
    background-color: transparent;
    box-shadow: none;
    ${(props) => props.$reader && 'border-radius: 0;'}
    @media (max-width: 765px) { margin: 12px; }
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && { padding: 0; overflow: visible; }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 880px;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  font-family: var(--font-family-ibm_plex_sans);
  ${(props) => props.$reader && `
    width: 1141px;
    max-width: calc(100vw - 64px);
    max-height: calc(100dvh - 64px);
    border-radius: 0;
    & > header { background: #fff; padding: 16px 20px; border-bottom: 1px solid rgba(201, 198, 204, .5); }
    & h2 { font-size: 23px; line-height: 30px; letter-spacing: -.23px; color: #4b464f; }
    @media (max-width: 765px) { max-width: calc(100vw - 24px); max-height: calc(100dvh - 24px); }
  `}
`;

const ModalHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fbf7fe;
  padding: 16px 24px;
`;

const HeaderTitle = styled.h2`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 18px;
  font-weight: 600;
  color: var(--text);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: ${(props) => props.$image ? '0' : '20px'};
  overflow: auto;
  min-height: 0;
  flex: 1;
  display: flex;
  justify-content: center;
`;

const ModalFooter = styled.footer`
  display: flex;
  justify-content: center;
  padding: 20px;
  border-top: 1px solid rgba(201, 198, 204, 0.5);
  flex-shrink: 0;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 65vh;
  object-fit: contain;
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 80vh;
  border: none;
`;

const PreviewText = styled.div`
  width: 100%;
  max-height: 80vh;
  overflow: auto;
  text-align: left;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  line-height: 24px;
  color: #181718;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 8px 0;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }
`;
